import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import type { Project } from '../../../services/projectManagement/projectsService';
import { pmIntakeFormsApiService, type PmIntakeForm, type PmIntakeTargetType } from '../../../services/projectManagement/pmIntakeFormsApiService';

interface IntakeFormsModalProps {
  organizationId: string;
  projects: Project[];
  onClose: () => void;
  onCreated: () => void;
}

const IntakeFormsModal: React.FC<IntakeFormsModalProps> = ({
  organizationId,
  projects,
  onClose,
  onCreated,
}) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<PmIntakeForm[]>([]);

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createTargetType, setCreateTargetType] = useState<PmIntakeTargetType>('task');
  const [creating, setCreating] = useState(false);

  const [selectedForm, setSelectedForm] = useState<PmIntakeForm | null>(null);
  const [submitProjectId, setSubmitProjectId] = useState('');
  const [submitTitle, setSubmitTitle] = useState('');
  const [submitDescription, setSubmitDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await pmIntakeFormsApiService.list(organizationId);
      if (error) {
        setForms([]);
      } else {
        setForms(data || []);
      }
    } catch {
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void fetchForms();
  }, [fetchForms]);

  useEffect(() => {
    setSubmitProjectId('');
    setSubmitTitle('');
    setSubmitDescription('');
  }, [selectedForm?.id]);

  const canSubmit = useMemo(() => {
    if (!selectedForm) return false;
    if (selectedForm.target_type === 'task') {
      return !!submitProjectId && !!submitTitle.trim();
    }
    return !!submitTitle.trim();
  }, [selectedForm, submitProjectId, submitTitle]);

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error('Form name is required');
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await pmIntakeFormsApiService.create({
        organization_id: organizationId,
        name,
        description: createDescription.trim() || null,
        target_type: createTargetType,
        config: {},
      });

      if (error || !data) {
        toast.error(String(error || 'Failed to create form'));
        return;
      }

      toast.success('Intake form created');
      setCreateName('');
      setCreateDescription('');
      setCreateTargetType('task');
      void fetchForms();
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (form: PmIntakeForm) => {
    if (!window.confirm(`Delete intake form "${form.name}"?`)) return;

    const { error } = await pmIntakeFormsApiService.delete(form.id, organizationId);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Intake form deleted');
    if (selectedForm?.id === form.id) {
      setSelectedForm(null);
    }
    void fetchForms();
  };

  const handleSubmit = async () => {
    if (!selectedForm) return;

    const payload: Record<string, any> = {};

    if (selectedForm.target_type === 'task') {
      payload.project_id = submitProjectId;
      payload.title = submitTitle.trim();
      payload.description = submitDescription.trim() || '';
    } else {
      payload.name = submitTitle.trim();
      payload.description = submitDescription.trim() || '';
    }

    setSubmitting(true);
    try {
      const { error } = await pmIntakeFormsApiService.submit(selectedForm.id, {
        organization_id: organizationId,
        payload,
      });

      if (error) {
        toast.error(String(error));
        return;
      }

      toast.success(selectedForm.target_type === 'task' ? 'Task created' : 'Project created');
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-5xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Intake Forms</h3>
            <div className="mt-1 text-sm text-gray-600">Collect requests and turn them into tasks or projects.</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Create intake form</div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={creating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={creating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Creates</label>
                  <select
                    value={createTargetType}
                    onChange={(e) => setCreateTargetType(e.target.value as PmIntakeTargetType)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={creating}
                  >
                    <option value="task">Task</option>
                    <option value="project">Project</option>
                  </select>
                </div>

                <button
                  onClick={() => void handleCreate()}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Form'}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Forms</div>

              {loading ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : forms.length === 0 ? (
                <div className="text-sm text-gray-500">No intake forms yet.</div>
              ) : (
                <div className="space-y-2">
                  {forms.map(f => {
                    const selected = selectedForm?.id === f.id;
                    return (
                      <div
                        key={f.id}
                        className={`rounded-lg border p-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'} cursor-pointer`}
                        onClick={() => setSelectedForm(f)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{f.name}</div>
                            {f.description ? (
                              <div className="mt-1 text-xs text-gray-600 line-clamp-2">{f.description}</div>
                            ) : null}
                            <div className="mt-2 text-xs text-gray-500">
                              Creates: {f.target_type}
                              {f.is_active ? ' · Active' : ' · Inactive'}
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            {user?.id && String(f.created_by) === String(user.id) ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleDelete(f);
                                }}
                                className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Submit request</div>

            {!selectedForm ? (
              <div className="text-sm text-gray-500">Select a form on the left.</div>
            ) : (
              <div className="space-y-3">
                {selectedForm.target_type === 'task' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                    <select
                      value={submitProjectId}
                      onChange={(e) => setSubmitProjectId(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      disabled={submitting}
                    >
                      <option value="">Select project...</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{selectedForm.target_type === 'task' ? 'Title' : 'Project name'}</label>
                  <input
                    value={submitTitle}
                    onChange={(e) => setSubmitTitle(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={submitDescription}
                    onChange={(e) => setSubmitDescription(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={submitting}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setSelectedForm(null)}
                    className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                    disabled={submitting}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => void handleSubmit()}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                    disabled={!canSubmit || submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeFormsModal;
